import { Category } from '~/models/product.js'
import type { CartItem } from '../models/cartItem.js'

export const useProductLocalStorage = () => {
  const localStorageKey = 'checkoutCart'

  const getCartFromLocalStorage = (): CartItem[] => {
    const cart = localStorage.getItem(localStorageKey)
    if (!cart) return []

    try {
      const parsed = JSON.parse(cart)

      if (!Array.isArray(parsed)) return []

      return parsed.map((item): CartItem => ({
        id: String(item.id ?? ''),
        quantity: Number(item.quantity) || 0,
        product: {
          id: String(item.product?.id ?? ''),
          images: item.product?.images,
          name: String(item.product?.name ?? ''),
          subtitle: item.product?.subtitle ?? undefined,
          description: item.product?.description ?? undefined,
          weight: item.product?.weight !== undefined ? Number(item.product.weight) : undefined,
          length: item.product?.length !== undefined ? Number(item.product.length) : undefined,
          size: item.product?.size ?? undefined,
          color: item.product?.color ?? undefined,
          category: Object.values(Category).includes(item.product?.category)
            ? item.product.category
            : Category.GEAR, // fallback default
          price: Number(item.product?.price) || 0,
        },
      }))
    }
    catch (error) {
      console.warn('Failed to parse cart from localStorage:', error)
      return []
    }
  }

  const addItemToLocalStorage = (item: CartItem): void => {
    const cart = getCartFromLocalStorage()
    const existingItemIndex = cart.findIndex(cartItem => cartItem.id === item.id)

    if (existingItemIndex !== -1) {
      // Update quantity if item already exists
      cart[existingItemIndex].quantity += item.quantity
    }
    else {
      // Add new item to cart
      cart.push(item)
    }

    saveCartToLocalStorage(cart)
  }

  const removeItemFromLocalStorage = (itemId: string): void => {
    const cart = getCartFromLocalStorage()
    const updatedCart = cart.filter(item => item.id !== itemId)
    saveCartToLocalStorage(updatedCart)
  }

  const saveCartToLocalStorage = (cart: CartItem[]): void => {
    localStorage.setItem(localStorageKey, JSON.stringify(cart))
  }

  const clearCart = (): void => {
    localStorage.removeItem(localStorageKey)
  }

  return {
    removeItemFromLocalStorage,
    addItemToLocalStorage,
    getCartFromLocalStorage,
    saveCartToLocalStorage,
    clearCart,
  }
}
